// reactmodal
import React, { useContext, useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'

import { useLazyQuery } from '@apollo/client'

import { useLocation } from 'react-router-dom'

// contexts
import AuthContext from '../../../../utils/authProvider'

import {
  notificationSuccess,
  notificationError
} from '../../../../utils/notification'

// ant design
import {
  Card,
  Form,
} from 'antd'

// operations
import { QUERY_DISTRICT_REPORTS } from '../../../../operations/queries/districtreports'
import { QUERY_DISTRICT_METRICS_REPORT } from '../../../../operations/queries/reportBuilder/districtMetricsReport'

// components
import '../common/ReportBuilder.css'

// other libraries
import formatErrors from '../../../../utils/form'
import FormContainer from '../../../FormContainer'

//Functions
import { LayoutContext } from 'antd/lib/layout/layout'

//Components
import ReportDefinition from '../common/ReportDefinition'
import ReportTable from '../common/ReportTable'
import DistrictsSelector from '../common/DistrictsSelector'

const ReportForm = ({ isMountedRef }) => {
  const [form] = Form.useForm()

  //--------
  //Auth
  //--------
  const Auth = useContext(AuthContext)
  const isSuperAdmin = Auth.hasRole('SUPER_ADMIN')
  const isSchoolsGeneral = Auth.hasRole('SCHOOLS_GENERAL')
  const isSchoolsAdmin = Auth.hasRole('SCHOOLS_ADMIN')
  const isDistrictsAdmin = Auth.hasRole('DISTRICTS_ADMIN')

  // const districtIds = isSuperAdmin ? [Auth?.districtsAdmin.map(x => x.id)] : [Auth?.districtsAdmin.map(x => x.id)] //Set to user current district
  const districtIds = Auth?.districtsAdmin.map(x => x.id)

  //--------
  //Report definition state/ref
  //--------
  const reportDefsRef = useRef()
  const reportDefRef = useRef({})
  const [reportDefChanged, setReportDefChanged] = useState(false)
  const [selectedReportDefId, setSelectedReportDefId] = useState()
  const [selectedDistrictIds, setSelectedDistrictIds] = useState([])
  const [selectedFieldNames, setSelectedFieldNames] = useState([])

  //--------
  //Report preview
  //--------
  const [reportPreviewTitle, setReportPreviewTitle] = useState('')
  const [reportPreviewNotes, setReportPreviewNotes] = useState('')

  //--------
  //Report table state/ref
  //--------
  const [reportTableColumns, setReportTableColumns] = useState([])
  const [reportTableExtraData, setreportTableExtraData] = useState([])
  const [reportTableExtraDatasource,setreportTableExtraDatasource] = useState([])
  const [reportTableHasFilters, setReportTableHasFilters] = useState(false);
  const [reportTableHasSorts, setReportTableHasSorts] = useState(false);
  const [reportTableSortState, setReportTableSortState] = useState({field: null, order: null});

  //--------
  //Report data
  //--------
  const [queryResults, { loading: queryLoading }] = useLazyQuery(
    QUERY_DISTRICT_METRICS_REPORT,
    {
      variables: {
        districtIds: selectedDistrictIds,
        selectedFields: selectedFieldNames,
      },
      fetchPolicy: 'cache-first',
    }
  )

  const setIds = async () => {
    setSelectedDistrictIds(reportDefRef?.current?.reportParameters?.selectedDistrictIds)
    const selectedFieldNames = reportDefRef?.current?.selectedFields ? reportDefRef?.current?.selectedFields.map((field) => field.fieldName) : []
    setSelectedFieldNames(selectedFieldNames)
  }

  const queryReportData = async () => {
    if (!isMountedRef.current) return [];

    //Set filters and validate
    await setIds()  //Calling a separate function with await so that we can be sure state is updated before useLazyQuery call.  Otherwise parms will be empty when calling.

    if (!reportDefRef?.current?.selectedFields || reportDefRef?.current?.selectedFields.length === 0) {
      notificationError('At least one column must be selected.')
      return
    }
    if (!reportDefRef?.current?.reportParameters?.selectedDistrictIds || reportDefRef?.current?.reportParameters?.selectedDistrictIds.length === 0) {
      notificationError('At least one district must be selected.')
      return
    }
    
    //Get report data
    let reportDataArray = []
    const resultsData = await queryResults()
    if (resultsData) {
      const reportDataJson = resultsData?.data?.districtMetricsReport?.reportDataJson
      reportDataArray = reportDataJson ? JSON.parse(reportDataJson) : [];
      if(resultsData?.data?.schoolMetricsReport?.message === 'Unauthorized selections removed') {
        notification.warning({
          message: 'Warning',
          description: 'Unauthorized school selections were removed from your data.',
          duration: 5, // in seconds
        });
      }
    }
    setreportTableExtraDatasource(reportDataArray)
    setreportTableExtraData(reportDataArray)
    return reportDataArray
  }

  return (
    <Form 
      form={form} name='districtMetricsReport' 
      layout='horizontal'  
      labelWrap
      labelCol={{ xs: { span: 8 }, sm: { span: 6 }, md: { span: 5 }, lg: { span: 4 }, xl: { span: 3 } }}
      wrapperCol={{ xs: { span: 16 }, sm: { span: 17 }, md: { span: 18 }, lg: { span: 19 }, xl: { span: 20 } }}
    >
      <div className='page-container'>
        <Card
          title='Report Definition'
          style={{
            marginBottom: 30
          }}
        >
          <ReportDefinition
            form={form}
            reportGroup={'Districts'}
            DataFiltersComponent={DistrictsSelector}
            districtIds={districtIds}

            reportDefsRef={reportDefsRef}
            reportDefRef={reportDefRef}
            selectedReportDefId={selectedReportDefId}
            setSelectedReportDefId={setSelectedReportDefId}

            reportPreviewTitle={reportPreviewTitle}
            setReportPreviewTitle={setReportPreviewTitle}
            reportPreviewNotes={reportPreviewNotes}
            setReportPreviewNotes={setReportPreviewNotes}

            reportTableColumns={reportTableColumns}
            setReportTableColumns={setReportTableColumns}
            reportTableExtraDatasource={reportTableExtraDatasource}
            setreportTableExtraDatasource={setreportTableExtraDatasource}
            reportTableExtraData={reportTableExtraData}
            setreportTableExtraData={setreportTableExtraData}
            reportTableHasFilters={reportTableHasFilters}
            setReportTableHasFilters={setReportTableHasFilters}
            reportTableHasSorts={reportTableHasSorts}
            setReportTableHasSorts={setReportTableHasSorts}
            reportTableSortState={reportTableSortState}
            setReportTableSortState={setReportTableSortState}

            DataFiltersComponentName={'DistrictsSelector'}
            DataFiltersComponentLabel={'Select District(s)'}

            reportDefChanged={reportDefChanged}
            setReportDefChanged={setReportDefChanged}

            name={'reportDef'}
            label={'Report Definition'}

            isMountedRef={isMountedRef}

          />
        </Card>

        <Card
          title='Report Preview'
          style={{
            marginBottom: 30
          }}
        >
          <ReportTable
            form={form}
            reportGroup={'Districts'}
            queryReportData={queryReportData}

            reportDefsRef={reportDefsRef}
            reportDefRef={reportDefRef}
            selectedReportDefId={selectedReportDefId}
            setSelectedReportDefId={setSelectedReportDefId}

            reportPreviewTitle={reportPreviewTitle}
            reportPreviewNotes={reportPreviewNotes}
            reportTableColumns={reportTableColumns}
            setReportTableColumns={setReportTableColumns}
            reportTableExtraData={reportTableExtraData}
            setreportTableExtraData={setreportTableExtraData}
            reportTableExtraDatasource={reportTableExtraDatasource}
            setreportTableExtraDatasource={setreportTableExtraDatasource}
            reportTableHasFilters={reportTableHasFilters}
            setReportTableHasFilters={setReportTableHasFilters}
            reportTableHasSorts={reportTableHasSorts}
            setReportTableHasSorts={setReportTableHasSorts}
            reportTableSortState={reportTableSortState}
            setReportTableSortState={setReportTableSortState}

            reportDefChanged={reportDefChanged}
            setReportDefChanged={setReportDefChanged}

            reportTableName={'reportTable'}
            reportTableLabel={'Report Table'}
            reportTableStyle={{ width: '100%', marginTop: '16px' }}
            reportTableScroll={{ x: 'max-content' }}
            reportTableVisible={true}

            isMountedRef={isMountedRef}

          />
        </Card>
      </div>
    </Form>
  )
}

// ReportForm.propTypes = {
//   data: PropTypes.object.isRequired
// }

const DistrictMetricsReport = props => {
  const location = useLocation()

  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  //Following two lines get reportDefId from URL if we pass in url.
  //const pathnameParts = location.pathname.split('/')
  //const reportDefId = pathnameParts[pathnameParts.length - 1];
  const reportDefId = location?.state?.reportDefId

  return (
    <>
      <FormContainer
        form={<ReportForm reportGroup='Districts' isMountedRef={isMountedRef}/>}
        loading={false}
        size={1}
      />
    </>
  )
}

export default DistrictMetricsReport
